import { useState } from "react";
import {
  Zap,
  Phone,
  MessageSquare,
  Mail,
  Database,
  Clock,
  UserCircle2,
  AtSign,
  Send,
  ShieldOff,
  RefreshCcw,
  Quote,
  ArrowRight,
  Sparkles,
  TrendingUp,
  Target,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from "lucide-react";

/* --- Data ---------------------------------------------------------- */

const SEQUENCES = {
  "fast-track": {
    id: "fast-track",
    label: "Fast-Track",
    tabTitle: "Fast-Track (Hot Leads, 3 days)",
    tier: "Hot Leads",
    duration: "3 days",
    accent: "#00C2A8",
    steps: [
      {
        id: "step-entry",
        day: "Day 0",
        label: "Entry",
        Icon: Database,
        agent: "Intake Scorer",
        action: "Tag as HOT, assign Fast-Track",
        channel: "CRM",
        title: "Lead Scored 80+",
      },
      {
        id: "step-voice",
        day: "Day 1",
        label: "Voice Call",
        Icon: Phone,
        agent: "AI Voice Agent (Vapi)",
        channel: "Voice",
        title: "AI Voice Agent (Vapi)",
        script:
          "\"Hi [Name], this is Alex from [Company]. I saw you stopped by our booth at MedTech Summit — wanted to reach out personally about how our [product] is helping surgical teams reduce setup time. Do you have 90 seconds?\"",
        outcomes: [
          { tone: "green", Icon: CheckCircle2, label: "Answered", detail: "qualify" },
          { tone: "amber", Icon: AlertTriangle, label: "No answer", detail: "leave voicemail + trigger SMS" },
          { tone: "muted", Icon: XCircle, label: "Voicemail full", detail: "skip to email" },
        ],
      },
      {
        id: "step-sms",
        day: "Day 2",
        label: "SMS",
        Icon: MessageSquare,
        agent: "Make.com SMS Agent",
        channel: "SMS",
        title: "Make.com SMS Agent",
        message:
          "Hi [Name], Alex here — sent you a voicemail yesterday. Quick question: is reducing device setup time a priority for your team in Q3? Reply YES and I'll send our 2-min overview.",
        badge: "98% open rate channel",
        outcomes: [
          { tone: "green", Icon: CheckCircle2, label: "Replied YES", detail: "send overview + book link" },
          { tone: "amber", Icon: AlertTriangle, label: "No reply", detail: "queue Day 3 email" },
        ],
      },
      {
        id: "step-email",
        day: "Day 3",
        label: "Email + Booking Link",
        Icon: Mail,
        agent: "Make.com Email Agent",
        channel: "Email",
        title: "Make.com Email Agent",
        subject: "Still worth 15 minutes, [Name]?",
        emailBody:
          "Hi [Name], I know your quarter is packed — but if there's any chance shaving 20 min off device setup could help your surgical team this month, our 15-min walkthrough will show you exactly how [Company] is doing it today.",
        cta: "Book a 15-min demo →",
        outcomes: [
          { tone: "green", Icon: CheckCircle2, label: "Booked", detail: "CONVERTED · notify rep" },
          { tone: "blue", Icon: RefreshCcw, label: "Clicked, not booked", detail: "re-queue Day 5" },
          { tone: "muted", Icon: XCircle, label: "No open", detail: "disqualify check" },
        ],
      },
    ],
    stats: [
      { id: "stat-conv", label: "Avg Conversion Rate (Fast-Track)", value: "31%", note: "vs. 2% unmanaged", accent: "#00C2A8" },
      { id: "stat-time", label: "Avg Time to Demo Booked", value: "1.8 days", note: "voice + SMS + email combined", accent: "#0EA5E9" },
      { id: "stat-count", label: "Leads Converted This Month", value: "18", note: "of 58 fast-tracked", accent: "#00C2A8" },
    ],
  },

  standard: {
    id: "standard",
    label: "Standard",
    tabTitle: "Standard (Warm Leads, 7 days)",
    tier: "Warm Leads",
    duration: "7 days",
    accent: "#0EA5E9",
    steps: [
      {
        id: "step-s-entry",
        day: "Day 0",
        label: "Entry",
        Icon: Database,
        agent: "Intake Scorer",
        action: "Tag as WARM, assign Standard",
        channel: "CRM",
        title: "Lead Scored 50–79",
      },
      {
        id: "step-s-email1",
        day: "Day 1",
        label: "Intro Email",
        Icon: Mail,
        agent: "Make.com Email Agent",
        channel: "Email",
        title: "Make.com Email Agent",
        subject: "Nice meeting you at MedTech, [Name]",
        outcomes: [
          { tone: "green", Icon: CheckCircle2, label: "Replied", detail: "route to rep" },
          { tone: "muted", Icon: XCircle, label: "No reply", detail: "queue Day 3 SMS" },
        ],
      },
      {
        id: "step-s-sms",
        day: "Day 3",
        label: "SMS Nudge",
        Icon: MessageSquare,
        agent: "Make.com SMS Agent",
        channel: "SMS",
        title: "Make.com SMS Agent",
        badge: "98% open rate channel",
        message:
          "Hi [Name] — sent you a quick email Monday. Worth a look when you have 90 seconds? Reply STOP to opt out.",
        outcomes: [
          { tone: "green", Icon: CheckCircle2, label: "Replied YES", detail: "send booking link" },
          { tone: "amber", Icon: AlertTriangle, label: "No reply", detail: "queue Day 5 voice" },
        ],
      },
      {
        id: "step-s-voice",
        day: "Day 5",
        label: "Voice Touch",
        Icon: Phone,
        agent: "AI Voice Agent (Vapi)",
        channel: "Voice",
        title: "AI Voice Agent (Vapi)",
        script:
          "\"Hi [Name], just checking in from [Company] — wanted to make sure my note got through. Any interest in a quick walkthrough this week?\"",
        outcomes: [
          { tone: "green", Icon: CheckCircle2, label: "Answered", detail: "qualify + book" },
          { tone: "amber", Icon: AlertTriangle, label: "Voicemail", detail: "queue Day 7 email" },
        ],
      },
      {
        id: "step-s-email2",
        day: "Day 7",
        label: "Final Email",
        Icon: Mail,
        agent: "Make.com Email Agent",
        channel: "Email",
        title: "Make.com Email Agent",
        subject: "Last check-in, [Name] — worth 10 min?",
        cta: "Grab a 15-min slot →",
        outcomes: [
          { tone: "green", Icon: CheckCircle2, label: "Booked", detail: "CONVERTED" },
          { tone: "muted", Icon: XCircle, label: "No engagement", detail: "tag COLD, archive" },
        ],
      },
    ],
    stats: [
      { id: "stat-conv", label: "Avg Conversion Rate (Standard)", value: "17%", note: "vs. 2% unmanaged", accent: "#0EA5E9" },
      { id: "stat-time", label: "Avg Time to Demo Booked", value: "5.2 days", note: "4-touch cadence", accent: "#0EA5E9" },
      { id: "stat-count", label: "Leads Converted This Month", value: "11", note: "of 64 warm-tracked", accent: "#0EA5E9" },
    ],
  },

  "long-nurture": {
    id: "long-nurture",
    label: "Long-Nurture",
    tabTitle: "Long-Nurture (Cold Leads, 14 days)",
    tier: "Cold Leads",
    duration: "14 days",
    accent: "#F59E0B",
    steps: [
      {
        id: "step-l-entry",
        day: "Day 0",
        label: "Entry",
        Icon: Database,
        agent: "Intake Scorer",
        action: "Tag as COLD, assign Long-Nurture",
        channel: "CRM",
        title: "Lead Scored 0–49",
      },
      {
        id: "step-l-e1",
        day: "Day 1",
        label: "Value Email #1",
        Icon: Mail,
        agent: "Make.com Email Agent",
        channel: "Email",
        title: "Case Study Drop",
        subject: "How Regional Surgical cut setup time 22%",
        outcomes: [
          { tone: "green", Icon: CheckCircle2, label: "Opened + clicked", detail: "escalate to WARM" },
          { tone: "muted", Icon: XCircle, label: "No open", detail: "continue nurture" },
        ],
      },
      {
        id: "step-l-e2",
        day: "Day 4",
        label: "Value Email #2",
        Icon: Mail,
        agent: "Make.com Email Agent",
        channel: "Email",
        title: "Industry Benchmark",
        subject: "Benchmark: what top med device teams do differently",
        outcomes: [
          { tone: "green", Icon: CheckCircle2, label: "Engaged", detail: "escalate to WARM" },
          { tone: "muted", Icon: XCircle, label: "Silent", detail: "continue nurture" },
        ],
      },
      {
        id: "step-l-e3",
        day: "Day 7",
        label: "Value Email #3",
        Icon: Mail,
        agent: "Make.com Email Agent",
        channel: "Email",
        title: "Peer Story",
        subject: "A note from Dr. Webb at Regional Surgical",
        outcomes: [
          { tone: "green", Icon: CheckCircle2, label: "Replied", detail: "route to rep" },
          { tone: "muted", Icon: XCircle, label: "Silent", detail: "continue nurture" },
        ],
      },
      {
        id: "step-l-e4",
        day: "Day 10",
        label: "Value Email #4",
        Icon: Mail,
        agent: "Make.com Email Agent",
        channel: "Email",
        title: "Soft CTA",
        subject: "Two-min product demo (no call needed)",
        outcomes: [
          { tone: "green", Icon: CheckCircle2, label: "Watched", detail: "escalate to WARM" },
          { tone: "muted", Icon: XCircle, label: "Silent", detail: "continue nurture" },
        ],
      },
      {
        id: "step-l-e5",
        day: "Day 13",
        label: "Re-score",
        Icon: RefreshCcw,
        agent: "Scoring Agent",
        channel: "System",
        title: "Engagement Re-score",
        action: "Recalculate score based on 14-day behavior",
        outcomes: [
          { tone: "green", Icon: CheckCircle2, label: "Score ≥ 50", detail: "promote to Standard" },
          { tone: "muted", Icon: XCircle, label: "Still cold", detail: "archive, re-check 90d" },
        ],
      },
    ],
    stats: [
      { id: "stat-conv", label: "Avg Conversion Rate (Long-Nurture)", value: "6%", note: "still 3x unmanaged", accent: "#F59E0B" },
      { id: "stat-time", label: "Avg Time to Demo Booked", value: "12.4 days", note: "cold → warm → demo", accent: "#0EA5E9" },
      { id: "stat-count", label: "Leads Converted This Month", value: "2", note: "of 34 cold-tracked", accent: "#F59E0B" },
    ],
  },
};

const CONTROLS = [
  { Icon: Clock, label: "Send Window", value: "8am – 6pm", detail: "prospect's local time zone" },
  { Icon: Phone, label: "Voice Agent", value: "Vapi", detail: "voice-clone enabled" },
  { Icon: AtSign, label: "Email Sender", value: "Rep's name & inbox", detail: "connected via n8n" },
  { Icon: Send, label: "SMS Sender", value: "Dedicated number", detail: "Make.com Twilio integration" },
  { Icon: ShieldOff, label: "Disqualification", value: "No engagement after final touch", detail: "tag COLD, archive" },
  { Icon: RefreshCcw, label: "Re-entry Rule", value: "Re-score in 90 days", detail: "if tagged COLD" },
];

/* --- Helpers ------------------------------------------------------- */

const channelColors = {
  CRM: "#7A9BB5",
  Voice: "#00C2A8",
  SMS: "#0EA5E9",
  Email: "#F59E0B",
  System: "#7A9BB5",
};

function OutcomePill({ tone, Icon, label, detail }) {
  const palette = {
    green: { c: "#22C55E", bg: "rgba(34, 197, 94, 0.08)", bd: "rgba(34, 197, 94, 0.25)" },
    amber: { c: "#F59E0B", bg: "rgba(245, 158, 11, 0.08)", bd: "rgba(245, 158, 11, 0.25)" },
    blue:  { c: "#0EA5E9", bg: "rgba(14, 165, 233, 0.08)", bd: "rgba(14, 165, 233, 0.25)" },
    muted: { c: "#7A9BB5", bg: "rgba(122, 155, 181, 0.06)", bd: "rgba(122, 155, 181, 0.20)" },
  }[tone];
  return (
    <div
      className="flex items-start gap-2 rounded-md"
      style={{
        padding: "6px 9px",
        background: palette.bg,
        border: `1px solid ${palette.bd}`,
      }}
    >
      <Icon size={12} color={palette.c} style={{ marginTop: 2, flexShrink: 0 }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: "Inter, sans-serif", fontSize: 11.5, color: palette.c, fontWeight: 600, lineHeight: 1.2 }}>
          {label}
        </div>
        <div style={{ fontFamily: "Inter, sans-serif", fontSize: 11, color: "var(--text-secondary)", lineHeight: 1.35 }}>
          {detail}
        </div>
      </div>
    </div>
  );
}

function ChannelBadge({ channel }) {
  const c = channelColors[channel] || "#7A9BB5";
  return (
    <span
      className="pill"
      style={{
        color: c,
        background: `${c}12`,
        borderColor: `${c}30`,
        fontSize: 10,
      }}
    >
      <span className="pill-dot" style={{ background: c }} />
      {channel}
    </span>
  );
}

/* --- Sub-components ----------------------------------------------- */

function Tabs({ active, onChange }) {
  return (
    <div
      data-testid="sequence-tabs"
      className="flex flex-wrap gap-2 p-1.5 rounded-xl"
      style={{ background: "var(--bg-elevated)", border: "1px solid var(--border-divider)", width: "fit-content" }}
    >
      {Object.values(SEQUENCES).map((seq) => {
        const isActive = seq.id === active;
        return (
          <button
            key={seq.id}
            data-testid={`tab-${seq.id}`}
            onClick={() => onChange(seq.id)}
            className="rounded-lg"
            style={{
              padding: "9px 16px",
              background: isActive ? "rgba(0, 194, 168, 0.10)" : "transparent",
              border: isActive ? `1px solid ${seq.accent}40` : "1px solid transparent",
              color: isActive ? "var(--text-primary)" : "var(--text-secondary)",
              fontFamily: "Inter, sans-serif",
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
              transition: "all 150ms ease",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              letterSpacing: "-0.005em",
            }}
          >
            {isActive && (
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: seq.accent,
                  boxShadow: `0 0 10px ${seq.accent}`,
                }}
              />
            )}
            {seq.tabTitle}
          </button>
        );
      })}
    </div>
  );
}

function TimelineStep({ step, index, accent, isLast }) {
  const { Icon } = step;
  return (
    <div
      data-testid={step.id}
      className="flex-1 min-w-[280px] enter-up"
      style={{ animationDelay: `${index * 100}ms`, position: "relative" }}
    >
      {/* Day tag */}
      <div
        className="flex items-center gap-2 mb-3"
        style={{ paddingLeft: 2 }}
      >
        <span
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontWeight: 600,
            fontSize: 11,
            letterSpacing: "0.10em",
            color: accent,
            textTransform: "uppercase",
            padding: "3px 8px",
            borderRadius: 4,
            background: `${accent}12`,
            border: `1px solid ${accent}30`,
          }}
        >
          {step.day}
        </span>
        <span
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 12,
            color: "var(--text-secondary)",
            fontWeight: 500,
          }}
        >
          {step.label}
        </span>
      </div>

      {/* Node card */}
      <div
        className="card-glass p-4"
        style={{
          borderLeft: `3px solid ${accent}`,
          minHeight: 260,
          height: "100%",
        }}
      >
        {/* Node header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div
              className="flex items-center justify-center rounded-md"
              style={{ width: 28, height: 28, background: `${accent}12`, border: `1px solid ${accent}30` }}
            >
              <Icon size={14} color={accent} />
            </div>
            <div style={{ fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 600, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
              {step.title}
            </div>
          </div>
          <ChannelBadge channel={step.channel} />
        </div>

        {/* Agent */}
        <div className="mb-3">
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 9.5,
              letterSpacing: "0.12em",
              color: "var(--text-muted)",
              textTransform: "uppercase",
              marginBottom: 2,
            }}
          >
            Agent
          </div>
          <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--text-primary)" }}>
            {step.agent}
          </div>
        </div>

        {/* Body: script | message | email | action */}
        {step.script && (
          <div
            className="rounded-md px-3 py-2 mb-3"
            style={{
              background: "rgba(15, 25, 35, 0.5)",
              border: "1px dashed rgba(0, 194, 168, 0.30)",
            }}
          >
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 4 }}>
              Script
            </div>
            <div style={{ fontFamily: "Inter, sans-serif", fontStyle: "italic", fontSize: 11.5, lineHeight: 1.5, color: "var(--text-primary)" }}>
              {step.script}
            </div>
          </div>
        )}

        {step.message && (
          <div
            className="rounded-md px-3 py-2 mb-3"
            style={{
              background: "rgba(14, 165, 233, 0.06)",
              border: "1px solid rgba(14, 165, 233, 0.20)",
            }}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase" }}>
                SMS
              </div>
              {step.badge && (
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9,
                    color: "#0EA5E9",
                    padding: "1px 5px",
                    borderRadius: 3,
                    background: "rgba(14, 165, 233, 0.10)",
                  }}
                >
                  {step.badge}
                </span>
              )}
            </div>
            <div style={{ fontFamily: "Inter, sans-serif", fontSize: 11.5, lineHeight: 1.5, color: "var(--text-primary)" }}>
              {step.message}
            </div>
          </div>
        )}

        {step.subject && (
          <div
            className="rounded-md px-3 py-2 mb-3"
            style={{
              background: "rgba(245, 158, 11, 0.05)",
              border: "1px solid rgba(245, 158, 11, 0.20)",
            }}
          >
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 4 }}>
              Subject
            </div>
            <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12.5, color: "var(--text-primary)", fontWeight: 600, marginBottom: step.emailBody ? 6 : 0 }}>
              {step.subject}
            </div>
            {step.emailBody && (
              <div style={{ fontFamily: "Inter, sans-serif", fontSize: 11.5, lineHeight: 1.5, color: "var(--text-secondary)" }}>
                {step.emailBody}
              </div>
            )}
            {step.cta && (
              <div
                className="mt-2 inline-flex items-center gap-1.5"
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: 11.5,
                  color: "#00C2A8",
                  padding: "4px 8px",
                  borderRadius: 4,
                  background: "rgba(0, 194, 168, 0.10)",
                  border: "1px solid rgba(0, 194, 168, 0.30)",
                  fontWeight: 500,
                }}
              >
                {step.cta}
              </div>
            )}
          </div>
        )}

        {step.action && !step.subject && !step.script && !step.message && (
          <div className="mb-3">
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 2 }}>
              Action
            </div>
            <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--text-primary)" }}>
              {step.action}
            </div>
          </div>
        )}

        {/* Outcome paths */}
        {step.outcomes && (
          <div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 6 }}>
              Outcome Paths
            </div>
            <div className="flex flex-col gap-1.5">
              {step.outcomes.map((o, oi) => (
                <OutcomePill key={oi} {...o} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Arrow connector to next node */}
      {!isLast && (
        <div
          className="hidden xl:flex items-center justify-center"
          style={{
            position: "absolute",
            right: -22,
            top: "50%",
            transform: "translateY(-50%)",
            width: 44,
            zIndex: 2,
            color: accent,
          }}
        >
          <svg width="44" height="12" viewBox="0 0 44 12" fill="none">
            <line
              x1="0" y1="6" x2="36" y2="6"
              stroke={accent}
              strokeWidth="1.5"
              strokeDasharray="4 4"
              className="flow-line-static"
            >
              <animate attributeName="stroke-dashoffset" from="0" to="-16" dur="1.4s" repeatCount="indefinite" />
            </line>
            <path d="M32 1 L42 6 L32 11" stroke={accent} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      )}
    </div>
  );
}

function Timeline({ sequence }) {
  return (
    <article data-testid="sequence-timeline" className="card-glass p-6">
      <header className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2.5">
          <div
            className="flex items-center justify-center rounded-md"
            style={{ width: 28, height: 28, background: `${sequence.accent}12`, border: `1px solid ${sequence.accent}30` }}
          >
            <Zap size={14} color={sequence.accent} />
          </div>
          <div>
            <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 15, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
              {sequence.label} Sequence
            </h2>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase" }}>
              {sequence.tier} · {sequence.duration} · {sequence.steps.length} touchpoints
            </div>
          </div>
        </div>
        <span
          className="pill pill-teal"
        >
          <span className="pill-dot pill-dot-pulse" />
          Active
        </span>
      </header>

      <div
        className="flex flex-nowrap gap-8 xl:gap-11 overflow-x-auto pb-2"
        style={{ scrollSnapType: "x mandatory" }}
      >
        {sequence.steps.map((step, i) => (
          <div key={step.id} style={{ scrollSnapAlign: "start", flex: "1 1 280px", minWidth: 280 }}>
            <TimelineStep
              step={step}
              index={i}
              accent={sequence.accent}
              isLast={i === sequence.steps.length - 1}
            />
          </div>
        ))}
      </div>
    </article>
  );
}

function StatsRow({ sequence }) {
  const iconMap = [TrendingUp, Clock, Target];
  return (
    <section data-testid="sequence-stats" className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {sequence.stats.map((s, i) => {
        const Icon = iconMap[i] || TrendingUp;
        return (
          <article
            key={s.id}
            data-testid={`${sequence.id}-${s.id}`}
            className="card-glass card-interactive p-5 enter-up"
            style={{ animationDelay: `${i * 80}ms` }}
          >
            <div className="flex items-start justify-between mb-3">
              <div
                className="flex items-center justify-center rounded-lg"
                style={{ width: 32, height: 32, background: `${s.accent}12`, border: `1px solid ${s.accent}30` }}
              >
                <Icon size={14} color={s.accent} />
              </div>
            </div>
            <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 500, color: "var(--text-secondary)", marginBottom: 8 }}>
              {s.label}
            </div>
            <div className="stat-value" style={{ color: s.accent }}>
              {s.value}
            </div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10.5, color: "var(--text-muted)", marginTop: 4 }}>
              {s.note}
            </div>
          </article>
        );
      })}
    </section>
  );
}

function ControlsPanel({ sequence }) {
  return (
    <aside data-testid="sequence-controls" className="card-glass p-5" style={{ position: "sticky", top: 88 }}>
      <header className="flex items-center gap-2.5 mb-4">
        <div
          className="flex items-center justify-center rounded-md"
          style={{ width: 28, height: 28, background: "rgba(14, 165, 233, 0.10)", border: "1px solid rgba(14, 165, 233, 0.25)" }}
        >
          <Sparkles size={14} color="#0EA5E9" />
        </div>
        <div>
          <h3 style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 14, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
            Sequence Controls
          </h3>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase" }}>
            {sequence.label} · Live config
          </div>
        </div>
      </header>

      <ul className="space-y-3">
        {CONTROLS.map((c, i) => {
          const { Icon } = c;
          return (
            <li key={i} data-testid={`control-${i}`} className="enter-up" style={{ animationDelay: `${i * 50}ms` }}>
              <div className="flex items-start gap-3">
                <div
                  className="flex items-center justify-center rounded-md flex-shrink-0"
                  style={{
                    width: 26,
                    height: 26,
                    background: "rgba(30, 48, 72, 0.5)",
                    border: "1px solid var(--border-divider)",
                  }}
                >
                  <Icon size={12} color="#7A9BB5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 1 }}>
                    {c.label}
                  </div>
                  <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12.5, fontWeight: 500, color: "var(--text-primary)", lineHeight: 1.35 }}>
                    {c.value}
                  </div>
                  <div style={{ fontFamily: "Inter, sans-serif", fontSize: 11, color: "var(--text-muted)", lineHeight: 1.35, marginTop: 1 }}>
                    {c.detail}
                  </div>
                </div>
              </div>
              {i < CONTROLS.length - 1 && (
                <div style={{ height: 1, background: "rgba(30, 48, 72, 0.6)", marginTop: 12 }} />
              )}
            </li>
          );
        })}
      </ul>

      <div
        className="mt-5 pt-4 flex items-center justify-between"
        style={{ borderTop: "1px solid var(--border-divider)" }}
      >
        <div className="flex items-center gap-2">
          <UserCircle2 size={14} color="var(--text-muted)" />
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: "var(--text-muted)", letterSpacing: "0.06em" }}>
            LAST EDIT · 4h AGO
          </span>
        </div>
        <span
          className="pill pill-teal"
          style={{ fontSize: 10 }}
        >
          <span className="pill-dot pill-dot-pulse" />
          Live
        </span>
      </div>
    </aside>
  );
}

function InsightQuote() {
  return (
    <section
      data-testid="blueprint-insight"
      className="card-glass p-6 relative overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, rgba(0, 194, 168, 0.05) 0%, rgba(14, 165, 233, 0.04) 100%), var(--bg-elevated)",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: -30,
          right: -20,
          color: "rgba(0, 194, 168, 0.08)",
        }}
      >
        <Quote size={180} strokeWidth={1} />
      </div>

      <div className="relative">
        <div className="flex items-center gap-2 mb-4">
          <div
            className="flex items-center justify-center rounded-md"
            style={{ width: 26, height: 26, background: "rgba(0, 194, 168, 0.10)", border: "1px solid rgba(0, 194, 168, 0.30)" }}
          >
            <Quote size={12} color="#00C2A8" />
          </div>
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10,
              letterSpacing: "0.14em",
              color: "#00C2A8",
              textTransform: "uppercase",
            }}
          >
            Blueprint Insight Behind This Sequence
          </span>
        </div>

        <blockquote
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 18,
            fontWeight: 500,
            lineHeight: 1.55,
            letterSpacing: "-0.01em",
            color: "var(--text-primary)",
            maxWidth: 920,
            margin: 0,
          }}
        >
          "Automated follow-up systems produce conversion improvements ranging from{" "}
          <span style={{ color: "#0EA5E9" }}>20% (basic automation)</span> to{" "}
          <span style={{ color: "#00C2A8" }}>400% (comprehensive CRM implementation)</span>. Top-performing manufacturers hit{" "}
          <span style={{ color: "#00C2A8" }}>~21% lead conversion</span> — nearly{" "}
          <span style={{ color: "#00C2A8" }}>7x</span> the unmanaged industry average."
        </blockquote>

        <div
          className="mt-5 flex items-center gap-2"
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            color: "var(--text-secondary)",
            letterSpacing: "0.06em",
          }}
        >
          <ArrowRight size={12} color="#00C2A8" />
          AI Growth Infrastructure Blueprint · Insight 4
        </div>
      </div>
    </section>
  );
}

/* --- Page --------------------------------------------------------- */

export default function SequenceBuilder() {
  const [active, setActive] = useState("fast-track");
  const sequence = SEQUENCES[active];

  return (
    <div data-testid="sequence-builder-page" className="max-w-[1400px] mx-auto space-y-8">
      {/* Header */}
      <section data-testid="sequence-header" className="enter-up">
        <div className="flex items-start gap-4">
          <div
            style={{
              width: 4,
              alignSelf: "stretch",
              background: "linear-gradient(180deg, #00C2A8, #0EA5E9)",
              borderRadius: 4,
              minHeight: 68,
            }}
          />
          <div>
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                letterSpacing: "0.14em",
                color: "var(--accent-teal)",
                textTransform: "uppercase",
                marginBottom: 6,
              }}
            >
              Module · Multi-touch orchestration
            </div>
            <h1
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 800,
                fontSize: 28,
                letterSpacing: "-0.02em",
                color: "var(--text-primary)",
                lineHeight: 1.2,
                marginBottom: 8,
              }}
            >
              Nurture Sequence Builder
            </h1>
            <p
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: 14,
                lineHeight: 1.55,
                color: "var(--text-secondary)",
                maxWidth: 820,
              }}
            >
              Every touchpoint, every channel, every timing — configured once, executed automatically for every lead. Closing the 500-MQL-to-10-SQL gap without adding headcount.
            </p>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <Tabs active={active} onChange={setActive} />

      {/* Timeline + Right controls */}
      <section className="grid grid-cols-12 gap-6">
        <div className="col-span-12 xl:col-span-9 space-y-6">
          <Timeline sequence={sequence} />
          <StatsRow sequence={sequence} />
        </div>
        <div className="col-span-12 xl:col-span-3">
          <div style={{ maxWidth: 340 }}>
            <ControlsPanel sequence={sequence} />
          </div>
        </div>
      </section>

      {/* Insight quote */}
      <InsightQuote />
    </div>
  );
}
